import { Box, Chip, Stack, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { InviteRow } from "components/users/userAdminFilters";
import {
  clickableRowsSx,
  rowClickListener,
} from "components/users/usersDataGrid";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatLocalDateString } from "hooks/useFormatLocalDate";
import { CsvColumnDef } from "src/utils/csv";

const MAX_VISIBLE_PERMISSION_CHIPS = 4;

export const useInviteCsvColumns = (): CsvColumnDef<InviteRow>[] => {
  const { t } = useTenantTranslation();
  return [
    { header: t("userEmail"), value: (row) => row.email },
    {
      header: t("permissions"),
      value: (row) => row.permissions.join("; "),
    },
    { header: t("invitedAt"), value: (row) => row.invited_at },
    { header: t("acceptedAt"), value: (row) => row.accepted_at ?? "" },
    {
      header: t("status"),
      value: (row) => (row.is_active ? t("active") : t("inactive")),
    },
  ];
};

export const PendingInvitesDataGrid = ({
  rows,
  loading,
  selection,
  onSelectionChange,
  onRowClick,
  onCreateInviteLink,
  onSendInviteEmail,
  onDeleteInvite,
}: {
  rows: InviteRow[];
  loading: boolean;
  selection: string[];
  onSelectionChange: (ids: string[]) => void;
  onRowClick: (email: string) => void;
  onCreateInviteLink: (email: string) => void;
  onSendInviteEmail: (email: string) => void;
  onDeleteInvite: (email: string) => void;
}) => {
  const { t } = useTenantTranslation();
  const formatDate = useFormatLocalDateString("date");

  const columns: GridColDef<InviteRow>[] = [
    { field: "email", headerName: t("userEmail"), flex: 1.5 },
    {
      field: "permissions",
      headerName: t("permissions"),
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => {
        const visible = row.permissions.slice(0, MAX_VISIBLE_PERMISSION_CHIPS);
        const overflow = row.permissions.length - visible.length;
        return (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ flexWrap: "wrap", overflow: "hidden" }}
          >
            {visible.map((p) => (
              <Chip key={p} size="small" variant="outlined" label={p} />
            ))}
            {overflow > 0 ? (
              <Chip size="small" variant="outlined" label={`+${overflow}`} />
            ) : null}
          </Stack>
        );
      },
    },
    {
      field: "invited_at",
      headerName: t("invitedAt"),
      width: 140,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: "accepted_at",
      headerName: t("acceptedAt"),
      width: 140,
      valueGetter: (_value, row) => row.accepted_at ?? null,
      renderCell: ({ row }) =>
        row.accepted_at ? formatDate(row.accepted_at) : "—",
    },
    {
      field: "status",
      headerName: t("status"),
      width: 110,
      valueGetter: (_value, row) => (row.is_active ? "active" : "inactive"),
      renderCell: ({ row }) => (
        <Chip
          size="small"
          color={row.is_active ? "success" : "default"}
          label={row.is_active ? t("active") : t("inactive")}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      width: 60,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="email"
          label={t("sendInviteEmail")}
          onClick={() => {
            onSendInviteEmail(row.email);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          key="link"
          label={t("createInviteLink")}
          onClick={() => {
            onCreateInviteLink(row.email);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          key="delete"
          label={t("delete")}
          onClick={() => {
            onDeleteInvite(row.email);
          }}
          showInMenu
        />,
      ],
    },
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 400 }}>
      <Box sx={{ flex: 1, width: "100%" }}>
        <HotkeyCompliantDataGrid<InviteRow>
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.email}
          checkboxSelection
          pagination
          hideFooter={false}
          pageSizeOptions={[10, 25, 50, 100]}
          rowSelectionModel={{ type: "include", ids: new Set(selection) }}
          onRowSelectionModelChange={(model) => {
            onSelectionChange([...model.ids].map(String));
          }}
          onCellClick={rowClickListener(onRowClick)}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "invited_at", sort: "desc" }] },
          }}
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Stack
                sx={{ height: "100%" }}
                alignItems="center"
                justifyContent="center"
              >
                <Typography color="text.secondary">
                  {t("noPendingInvites")}
                </Typography>
              </Stack>
            ),
          }}
          sx={clickableRowsSx}
        />
      </Box>
    </Stack>
  );
};
